import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowRight, CurrencyDollar } from '@phosphor-icons/react';
import { setPageMeta } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Number(n).toLocaleString()}`;

export default function CostGuides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageMeta({
      title: 'Home Improvement Cost Guides (2026) | ConstructHelp',
      description: 'Real 2026 pricing for kitchen remodels, bathrooms, roofing, electrical, HVAC, decks, painting, and flooring — researched cost tables, FAQs, and how to compare quotes.',
      url: `${window.location.origin}/guides`,
    });
    axios.get(`${API_URL}/api/guides`)
      .then((r) => setGuides(r.data.guides || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="cost-guides-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="inline-flex items-center gap-2 bg-[#D95800] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-white shadow-[3px_3px_0px_#fff] mb-4">
            <BookOpen size={14} weight="fill" /> Cost Guides · 2026 Pricing
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight mb-3">
            Know the price <span className="text-[#D95800]">before you ask.</span>
          </h1>
          <p className="text-neutral-400 max-w-2xl">
            Researched national cost data for the most common home projects — what things cost,
            where budgets blow up, and how to compare contractor quotes like a pro.
          </p>
        </div>
      </div>

      {/* Guide grid */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        {loading ? (
          <p className="text-center text-neutral-500 py-12 font-bold">Loading guides...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="guides-grid">
            {guides.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                className="group bg-white dark:bg-[#0F172A] border-4 border-black shadow-[6px_6px_0px_#000] hover:shadow-[8px_8px_0px_#D95800] hover:-translate-y-0.5 transition-all p-6 block"
                data-testid={`guide-card-${g.slug}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-black uppercase tracking-widest bg-[#2B4C6F] text-white px-2 py-0.5 border border-black">
                    {g.trade}
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-black text-sm">
                    <CurrencyDollar size={16} weight="bold" />
                    {fmt(g.cost_low)}–{fmt(g.cost_high)}
                  </span>
                </div>
                <h2 className="text-lg font-black uppercase tracking-tight text-neutral-900 dark:text-white leading-snug mb-2">
                  {g.title}
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">{g.summary}</p>
                <span className="inline-flex items-center gap-1.5 text-[#D95800] font-black text-xs uppercase tracking-wide">
                  Read the guide <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-[#2B4C6F] border-4 border-black shadow-[8px_8px_0px_#000] p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-black uppercase text-white mb-2">
            Ready for real numbers on your project?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            National averages are a starting point. Get free itemized quotes from verified local
            contractors — compare them side-by-side, pay by milestone.
          </p>
          <Link
            to="/get-quotes"
            className="inline-flex items-center gap-2 h-13 px-8 py-3.5 bg-[#D95800] text-black font-black uppercase text-sm tracking-wide border-2 border-white shadow-[4px_4px_0px_#fff] hover:-translate-y-0.5 transition-transform"
            data-testid="guides-index-cta"
          >
            Get Free Quotes <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
